<script lang="ts">
	import SeoConfig from 'svelte-seo';
	import { createPageSEO } from '$lib/config/seo';

	import { Button } from '$lib/components/ui/button';
	import { Card, CardContent } from '$lib/components/ui/card';
	import { Badge } from '$lib/components/ui/badge';
	import { Smartphone, Star, Download, CheckCircle2, AlertCircle, Code2 } from '@lucide/svelte';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	const seo = createPageSEO({
		title: 'Hire Mobile Developers With Apps in Production',
		description:
			'iOS, Android, React Native developers with apps on the App Store. See their reviews, downloads, ratings.',
		canonical: '/hire-mobile-developers',
		keywords: 'hire mobile developer, iOS developer, Android developer, React Native engineer'
	});

	const featuredApps = [
		{
			developer: 'Jenny C.',
			role: 'Senior iOS Engineer',
			app: 'Fitness tracking app',
			stats: {
				downloads: '2M+',
				rating: '4.8',
				reviews: '45K',
				lastUpdate: '2 weeks ago'
			},
			codeAnalysis: {
				score: 92,
				highlights: [
					'Uses Swift 5.9 with async/await throughout',
					'Proper memory management - no retain cycles detected',
					'HealthKit integration follows Apple guidelines',
					'Error handling in 98% of network calls'
				]
			},
			tech: ['Swift', 'SwiftUI', 'Core Data', 'HealthKit']
		},
		{
			developer: 'Michael R.',
			role: 'React Native Lead',
			app: 'Food delivery marketplace',
			stats: {
				downloads: '500K+',
				rating: '4.6',
				reviews: '12K',
				lastUpdate: '1 week ago'
			},
			codeAnalysis: {
				score: 88,
				highlights: [
					'TypeScript strict mode enabled across entire codebase',
					'Implements lazy loading - initial bundle only 180KB',
					'Custom native modules for maps and camera',
					'85% test coverage with meaningful tests'
				]
			},
			tech: ['React Native', 'TypeScript', 'Redux', 'Maps API']
		},
		{
			developer: 'Alex K.',
			role: 'Android Engineer',
			app: 'Social networking app',
			stats: {
				downloads: '1M+',
				rating: '4.7',
				reviews: '28K',
				lastUpdate: '5 days ago'
			},
			codeAnalysis: {
				score: 90,
				highlights: [
					'Migrated 45K LOC from Java to Kotlin over 8 months',
					'Jetpack Compose with proper state hoisting',
					'Implements WorkManager for background sync',
					'Image loading optimized - uses Coil with caching'
				]
			},
			tech: ['Kotlin', 'Jetpack Compose', 'Room', 'Retrofit']
		}
	];

	const analysisCapabilities = [
		{
			category: 'Code Quality',
			items: [
				{
					label: 'Type safety',
					detail: 'TypeScript/Kotlin usage, strict mode',
					icon: CheckCircle2
				},
				{
					label: 'Code organization',
					detail: 'Feature-first vs domain structure',
					icon: CheckCircle2
				},
				{
					label: 'Code duplication',
					detail: 'Duplicate code blocks, similar functions',
					icon: AlertCircle
				},
				{ label: 'Error handling', detail: 'Try-catch coverage, null checks', icon: CheckCircle2 }
			]
		},
		{
			category: 'Security & Best Practices',
			items: [
				{ label: 'Secrets in code', detail: 'API keys, tokens hardcoded', icon: AlertCircle },
				{
					label: 'Dangerous patterns',
					detail: 'dangerouslySetInnerHTML, eval usage',
					icon: AlertCircle
				},
				{
					label: 'Permission handling',
					detail: 'Runtime permissions, info.plist',
					icon: CheckCircle2
				},
				{
					label: 'Data validation',
					detail: 'Input sanitization, schema validation',
					icon: CheckCircle2
				}
			]
		},
		{
			category: 'Performance',
			items: [
				{
					label: 'Bundle optimization',
					detail: 'Code splitting, tree shaking',
					icon: CheckCircle2
				},
				{ label: 'Memory management', detail: 'Retain cycles, bitmap handling', icon: AlertCircle },
				{
					label: 'Async patterns',
					detail: 'Proper async/await, background threads',
					icon: CheckCircle2
				},
				{
					label: 'List rendering',
					detail: 'FlatList optimization, RecyclerView',
					icon: CheckCircle2
				}
			]
		}
	];

	const seniorityIndicators = [
		{
			level: 'Junior',
			codePatterns: [
				'Inconsistent error handling',
				'Large components (500+ lines)',
				'Direct API calls in components',
				'No code splitting',
				'Mixed naming conventions'
			],
			score: '< 70'
		},
		{
			level: 'Mid',
			codePatterns: [
				'Consistent error boundaries',
				'Modular component structure',
				'Service layer for API calls',
				'Some dynamic imports',
				'TypeScript with basic types'
			],
			score: '70-84'
		},
		{
			level: 'Senior',
			codePatterns: [
				'Comprehensive error handling',
				'Clean architecture patterns',
				'Abstracted API layer with retry logic',
				'Optimized bundle splitting',
				'TypeScript strict mode + generics'
			],
			score: '85+'
		}
	];
</script>

<SeoConfig {...seo} />

<div class="flex min-h-[calc(100dvh-527px)] flex-col bg-gradient-to-b from-white to-gray-50">
	<main class="flex-1">
		<!-- Results-focused hero -->
		<section class="container mx-auto px-4 py-20">
			<div class="mx-auto max-w-4xl text-center">
				<Badge class="mb-4">
					<Smartphone class="mr-1 h-4 w-4" />
					Mobile Development
				</Badge>

				<h1 class="mb-6 text-4xl font-bold text-gray-900 md:text-6xl">
					Hire Mobile Developers<br />
					<span class="text-blue-600">With Analyzed Code + Published Apps</span>
				</h1>

				<p class="mb-4 text-xl text-gray-600">
					iOS, Android, React Native developers with apps on the App Store.
				</p>
				<p class="mb-8 text-lg text-gray-500">
					We analyze their code (TypeScript usage, architecture, performance patterns) and verify
					their app ratings before you interview.
				</p>

				<Button size="lg" href="/drop-jd" class="mb-8">Find Mobile Developers</Button>

				<!-- Quick stats -->
				<div class="mx-auto grid max-w-3xl grid-cols-2 gap-4 md:grid-cols-4">
					<div class="text-center">
						<div class="text-2xl font-bold text-blue-600">2,400+</div>
						<div class="text-xs text-gray-600">Developers</div>
						<div class="mt-1 text-xs text-gray-400">with analyzed code</div>
					</div>
					<div class="text-center">
						<div class="text-2xl font-bold text-blue-600">850+</div>
						<div class="text-xs text-gray-600">Published Apps</div>
						<div class="mt-1 text-xs text-gray-400">verified on stores</div>
					</div>
					<div class="text-center">
						<div class="text-2xl font-bold text-blue-600">4.6</div>
						<div class="text-xs text-gray-600">Avg. Rating</div>
						<div class="mt-1 text-xs text-gray-400">from real users</div>
					</div>
					<div class="text-center">
						<div class="text-2xl font-bold text-blue-600">89</div>
						<div class="text-xs text-gray-600">Avg. Code Score</div>
						<div class="mt-1 text-xs text-gray-400">from static analysis</div>
					</div>
				</div>
			</div>
		</section>

		<!-- What We Actually Analyze -->
		<section class="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
			<div class="container mx-auto px-4">
				<div class="mx-auto max-w-6xl">
					<div class="mb-12 text-center">
						<h2 class="mb-4 text-3xl font-bold">How We Know They're Good</h2>
						<p class="mx-auto max-w-2xl text-gray-600">
							Static analysis + LLM review of their actual code. Not claims on a resume—real
							patterns we detect in their repos.
						</p>
					</div>

					<div class="grid gap-6 md:grid-cols-3">
						{#each analysisCapabilities as category (category.category)}
							<Card class="shadow-none">
								<CardContent class="pt-0 sm:pt-0">
									<h3 class="mb-4 text-lg font-bold">{category.category}</h3>
									<div class="space-y-3">
										{#each category.items as item (item.label)}
											<div class="flex items-start gap-2">
												<svelte:component
													this={item.icon}
													class="mt-0.5 h-4 w-4 flex-shrink-0 {item.icon === CheckCircle2
														? 'text-green-600'
														: 'text-orange-600'}"
												/>
												<div>
													<div class="text-sm font-medium">{item.label}</div>
													<div class="text-xs text-gray-500">{item.detail}</div>
												</div>
											</div>
										{/each}
									</div>
								</CardContent>
							</Card>
						{/each}
					</div>

					<div class="mt-8 text-center">
						<Card class="inline-block max-w-2xl bg-white">
							<CardContent>
								<p class="text-sm text-gray-700">
									<Code2 class="mr-1 inline h-4 w-4" />
									<span class="font-bold">All analysis is automated.</span> We run ESLint, TypeScript
									compiler, bundle analyzers, and security scanners. Then LLM reviews patterns to determine
									seniority level.
								</p>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</section>

		<!-- Featured Apps / Developers with Analysis -->
		<section class="container mx-auto px-4 py-20">
			<div class="mx-auto max-w-6xl">
				<h2 class="mb-4 text-3xl font-bold">Developers with Verified Apps + Analyzed Code</h2>
				<p class="mb-12 text-gray-600">
					Real apps on the App Store + code analysis showing their actual patterns and quality.
				</p>

				<div class="space-y-6">
					{#each featuredApps as app (app.app)}
						<Card class="border-2 transition-colors hover:border-blue-200">
							<CardContent>
								<div class="grid gap-6 md:grid-cols-12">
									<!-- Developer Info -->
									<div class="md:col-span-3">
										<div class="mb-3 flex items-center gap-3">
											<div
												class="flex h-12 w-12 items-center justify-center superellipse bg-gradient-to-br from-blue-400 to-purple-400"
											>
												<Smartphone class="h-6 w-6 text-white" />
											</div>
											<div>
												<div class="font-bold">{app.developer}</div>
												<div class="text-sm text-gray-600">{app.role}</div>
											</div>
										</div>
										<div class="mb-3 text-sm text-gray-700">{app.app}</div>
										<div class="flex flex-wrap gap-1">
											{#each app.tech as tech (tech)}
												<Badge variant="outline" class="text-xs">{tech}</Badge>
											{/each}
										</div>
									</div>

									<!-- App Stats (Verified) -->
									<div class="md:col-span-3">
										<div class="mb-3 flex items-center gap-2">
											<Badge variant="outline" class="text-xs">
												<CheckCircle2 class="mr-1 h-3 w-3" />
												Verified
											</Badge>
										</div>
										<div class="space-y-2">
											<div class="flex items-center justify-between">
												<span class="flex items-center gap-1 text-sm text-gray-600">
													<Download class="h-4 w-4" />
													Downloads
												</span>
												<span class="font-bold text-blue-600">{app.stats.downloads}</span>
											</div>
											<div class="flex items-center justify-between">
												<span class="flex items-center gap-1 text-sm text-gray-600">
													<Star class="h-4 w-4" />
													Rating
												</span>
												<span class="font-bold text-yellow-600">{app.stats.rating} ⭐</span>
											</div>
											<div class="flex items-center justify-between">
												<span class="text-sm text-gray-600">Reviews</span>
												<span class="font-bold">{app.stats.reviews}</span>
											</div>
											<div class="flex items-center justify-between">
												<span class="text-sm text-gray-600">Updated</span>
												<span class="text-xs text-green-600">{app.stats.lastUpdate}</span>
											</div>
										</div>
									</div>

									<!-- Code Analysis -->
									<div class="md:col-span-6">
										<div class="mb-3 flex items-center gap-2">
											<Badge class="bg-purple-600 text-xs">
												Code Score: {app.codeAnalysis.score}/100
											</Badge>
											<span class="text-xs text-gray-500">from static analysis</span>
										</div>
										<div class="space-y-2">
											{#each app.codeAnalysis.highlights as highlight (highlight)}
												<div class="flex items-start gap-2">
													<CheckCircle2 class="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
													<p class="text-sm text-gray-700">{highlight}</p>
												</div>
											{/each}
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					{/each}
				</div>
			</div>
		</section>

		<!-- How We Determine Seniority -->
		<section class="bg-gray-50 py-20">
			<div class="container mx-auto px-4">
				<div class="mx-auto max-w-6xl">
					<h2 class="mb-4 text-center text-3xl font-bold">How We Determine Seniority from Code</h2>
					<p class="mb-12 text-center text-gray-600">
						These patterns are detectable through static analysis + LLM review. Not
						self-reported—actually measured.
					</p>

					<div class="grid gap-6 md:grid-cols-3">
						{#each seniorityIndicators as level (level.level)}
							<Card class="transition-shadow hover:shadow-lg">
								<CardContent>
									<div class="mb-4 flex items-center justify-between">
										<h3 class="text-xl font-bold">{level.level}</h3>
										<Badge variant="outline">{level.score}</Badge>
									</div>
									<div class="space-y-2">
										{#each level.codePatterns as pattern (pattern)}
											<div class="flex items-start gap-2">
												<div class="mt-2 h-1.5 w-1.5 flex-shrink-0 superellipse bg-blue-600"></div>
												<p class="text-sm text-gray-700">{pattern}</p>
											</div>
										{/each}
									</div>
								</CardContent>
							</Card>
						{/each}
					</div>

					<div class="mt-12 grid gap-6 md:grid-cols-2">
						<Card class="border-blue-200 bg-blue-50">
							<CardContent>
								<h3 class="mb-3 font-bold">📊 We Also Track Evolution</h3>
								<p class="mb-3 text-sm text-gray-700">
									By analyzing commit history, we can see if their code quality improved over time:
								</p>
								<ul class="space-y-1 text-sm text-gray-600">
									<li>• Error/LOC ratio trending down</li>
									<li>• Refactoring patterns (moving toward better architecture)</li>
									<li>• Test coverage increasing</li>
									<li>• TypeScript adoption in JavaScript projects</li>
								</ul>
							</CardContent>
						</Card>

						<Card class="border-purple-200 bg-purple-50">
							<CardContent>
								<h3 class="mb-3 font-bold">🔍 Mobile-Specific Analysis</h3>
								<p class="mb-3 text-sm text-gray-700">
									For mobile apps, we detect platform-specific patterns:
								</p>
								<ul class="space-y-1 text-sm text-gray-600">
									<li>• Memory management (retain cycles in iOS)</li>
									<li>• Proper async handling on main thread</li>
									<li>• Image optimization and caching</li>
									<li>• Battery-efficient location tracking</li>
								</ul>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</section>

		<!-- What You See vs Traditional Resume -->
		<section class="container mx-auto px-4 py-20">
			<div class="mx-auto max-w-5xl">
				<h2 class="mb-12 text-center text-3xl font-bold">
					What You See When Hiring Through TalentProfile
				</h2>

				<div class="mb-12 grid gap-6 md:grid-cols-2">
					<!-- Traditional Resume -->
					<Card class="shadow-none">
						<CardContent>
							<div class="mb-4">
								<Badge variant="outline">Traditional Resume</Badge>
							</div>
							<div class="space-y-4 text-sm">
								<div class="rounded border bg-white p-4">
									<div class="mb-1 font-bold">Mobile Developer</div>
									<div class="mb-3 text-xs text-gray-600">3 years experience</div>
									<div class="space-y-2 text-xs text-gray-600">
										<div>• "Expert in React Native and TypeScript"</div>
										<div>• "Strong code quality practices"</div>
										<div>• "Built scalable mobile applications"</div>
										<div>• "Performance optimization experience"</div>
									</div>
								</div>
								<div class="space-y-1 text-xs text-gray-500 italic">
									<div>❓ Do they actually use TypeScript?</div>
									<div>❓ What does "strong code quality" mean?</div>
									<div>❓ How many users have their apps?</div>
									<div>❓ Can we see their actual code patterns?</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<!-- TalentProfile -->
					<Card class="border-2 border-blue-200 shadow-lg">
						<CardContent>
							<div class="mb-4">
								<Badge class="bg-blue-600">TalentProfile</Badge>
							</div>
							<div class="space-y-4 text-sm">
								<div
									class="rounded border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 p-4"
								>
									<div class="mb-2 flex items-center justify-between">
										<div class="font-bold">Mobile Developer</div>
										<Badge class="bg-purple-600 text-xs">Score: 88/100</Badge>
									</div>
									<div class="mb-3 text-xs text-gray-600">3 years experience</div>

									<!-- Verified Stats -->
									<div class="mb-3 rounded bg-white p-2">
										<div class="mb-1 text-xs text-gray-500">Published App</div>
										<div class="text-sm font-bold text-blue-600">
											240K downloads • 4.6⭐ (3.2K reviews)
										</div>
										<div class="mt-1 text-xs text-gray-500">Last updated: 1 week ago</div>
									</div>

									<!-- Code Analysis -->
									<div class="space-y-1.5 text-xs text-gray-700">
										<div class="flex items-start gap-1">
											<CheckCircle2 class="mt-0.5 h-3 w-3 flex-shrink-0 text-green-600" />
											<span>TypeScript strict mode: <b>enabled</b></span>
										</div>
										<div class="flex items-start gap-1">
											<CheckCircle2 class="mt-0.5 h-3 w-3 flex-shrink-0 text-green-600" />
											<span>Bundle size: <b>180KB</b> (lazy loading implemented)</span>
										</div>
										<div class="flex items-start gap-1">
											<CheckCircle2 class="mt-0.5 h-3 w-3 flex-shrink-0 text-green-600" />
											<span>Test coverage: <b>85%</b></span>
										</div>
										<div class="flex items-start gap-1">
											<CheckCircle2 class="mt-0.5 h-3 w-3 flex-shrink-0 text-green-600" />
											<span>Error handling: <b>consistent</b> (98% of async calls)</span>
										</div>
									</div>
								</div>
								<div class="space-y-1 text-xs font-medium text-green-700">
									<div>✓ All stats verified from App Store API</div>
									<div>✓ Code analysis from actual repos</div>
									<div>✓ Patterns detected via static analysis + LLM</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				<!-- Value Props -->
				<div class="grid gap-6 md:grid-cols-3">
					<Card class="bg-white">
						<CardContent>
							<div class="mb-3 text-3xl">📊</div>
							<h3 class="mb-2 font-bold">Objective metrics</h3>
							<p class="text-sm text-gray-600">
								Not "strong coding skills"—actual TypeScript usage, bundle sizes, test coverage
								percentages from their repos.
							</p>
						</CardContent>
					</Card>

					<Card class="bg-white">
						<CardContent>
							<div class="mb-3 text-3xl">⚡</div>
							<h3 class="mb-2 font-bold">Skip the guessing</h3>
							<p class="text-sm text-gray-600">
								See their published apps AND code quality before interviewing. Screening 50 profiles
								takes 30 minutes instead of 3 hours.
							</p>
						</CardContent>
					</Card>

					<Card class="bg-white">
						<CardContent>
							<div class="mb-3 text-3xl">🎯</div>
							<h3 class="mb-2 font-bold">Better matches</h3>
							<p class="text-sm text-gray-600">
								Filter by actual patterns: "Show me developers who use TypeScript strict mode and
								have 80%+ test coverage."
							</p>
						</CardContent>
					</Card>
				</div>
			</div>
			<div class="mx-auto mt-10 max-w-4xl">
				<GlopayCtaHorizontal placement="hire-mobile-developers-post-hero" />
			</div>
		</section>

		<!-- Platform Breakdown -->
		<section class="bg-blue-50 py-20">
			<div class="container mx-auto px-4">
				<div class="mx-auto max-w-6xl">
					<h2 class="mb-12 text-center text-3xl font-bold">Developer Pool by Platform</h2>

					<div class="grid gap-6 md:grid-cols-3">
						<Card class="transition-shadow hover:shadow-lg">
							<CardContent>
								<div class="mb-4 text-center">
									<div class="mb-3 text-4xl">📱</div>
									<div class="mb-2 text-2xl font-bold text-blue-600">1,050+</div>
									<div class="mb-1 font-semibold">iOS Developers</div>
									<div class="text-xs text-gray-500">Avg. score: 86/100</div>
								</div>
								<div class="space-y-2 text-sm text-gray-700">
									<div class="mb-2 text-xs font-medium text-gray-600">
										Common Patterns Detected:
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-blue-600">•</span>
										<span>Swift 5.9+, async/await adoption</span>
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-blue-600">•</span>
										<span>Memory management (ARC, no cycles)</span>
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-blue-600">•</span>
										<span>SwiftUI state management patterns</span>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card class="transition-shadow hover:shadow-lg">
							<CardContent>
								<div class="mb-4 text-center">
									<div class="mb-3 text-4xl">🤖</div>
									<div class="mb-2 text-2xl font-bold text-green-600">980+</div>
									<div class="mb-1 font-semibold">Android Developers</div>
									<div class="text-xs text-gray-500">Avg. score: 84/100</div>
								</div>
								<div class="space-y-2 text-sm text-gray-700">
									<div class="mb-2 text-xs font-medium text-gray-600">
										Common Patterns Detected:
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-green-600">•</span>
										<span>Kotlin coroutines for async</span>
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-green-600">•</span>
										<span>Jetpack Compose adoption</span>
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-green-600">•</span>
										<span>Proper lifecycle handling</span>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card class="transition-shadow hover:shadow-lg">
							<CardContent>
								<div class="mb-4 text-center">
									<div class="mb-3 text-4xl">⚛️</div>
									<div class="mb-2 text-2xl font-bold text-purple-600">720+</div>
									<div class="mb-1 font-semibold">React Native Devs</div>
									<div class="text-xs text-gray-500">Avg. score: 87/100</div>
								</div>
								<div class="space-y-2 text-sm text-gray-700">
									<div class="mb-2 text-xs font-medium text-gray-600">
										Common Patterns Detected:
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-purple-600">•</span>
										<span>TypeScript strict mode enabled</span>
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-purple-600">•</span>
										<span>Bundle optimization (code splitting)</span>
									</div>
									<div class="flex items-start gap-2">
										<span class="font-bold text-purple-600">•</span>
										<span>Custom native modules</span>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</section>

		<!-- CTA -->
		<section class="container mx-auto px-4 py-20">
			<Card
				class="mx-auto max-w-4xl border-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white"
			>
				<CardContent class="px-8 py-12 pt-6">
					<div class="mb-8 text-center">
						<h2 class="mb-4 text-3xl font-bold md:text-4xl">
							See Their Code Quality Before You Interview
						</h2>
						<p class="mb-2 text-lg opacity-90">
							2,400+ developers with verified apps + analyzed code
						</p>
						<p class="text-base opacity-80">
							Filter by TypeScript usage, test coverage, bundle optimization, and more. All
							automatically detected.
						</p>
					</div>

					<div class="mb-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
						<Button size="lg" variant="secondary" href="/drop-jd" class="w-full sm:w-auto">
							Drop Your Job Description
						</Button>
						<!-- <Button size="lg" variant="outline" class="w-full sm:w-auto border-white text-white hover:bg-white/10" href="/browse-profiles">
              Browse Analyzed Profiles
            </Button> -->
					</div>

					<div class="grid gap-6 border-t border-white/20 pt-8 md:grid-cols-3">
						<div class="text-center">
							<div class="mb-1 text-2xl font-bold">Automated analysis</div>
							<div class="text-sm opacity-80">ESLint + TypeScript + LLM review</div>
						</div>
						<div class="text-center">
							<div class="mb-1 text-2xl font-bold">Verified apps</div>
							<div class="text-sm opacity-80">Real ratings from App Store API</div>
						</div>
						<div class="text-center">
							<div class="mb-1 text-2xl font-bold">Objective metrics</div>
							<div class="text-sm opacity-80">Not resume claims, actual patterns</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</section>
	</main>
</div>
